import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import AdminIdRow, { AdminEditMeta } from '@/components/AdminIdRow';
import ComparisonHeader from '@/components/ComparisonHeader';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft, AlertTriangle, Clock, ShieldCheck, Save, Flag, CheckCircle2,
  Play, Pause, Volume2, History, Star, FileText, Image as ImageIcon,
  Calendar, Hash, CheckSquare, ListChecks, Trash2, Plus, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import SatisactionSection from '@/pages/qa/SatisactionSection';
import QAActionsBar from '@/pages/qa/QAActionsBar';
import QualifierPill from '@/components/QualifierPill';
import { computeQuestionNumbers } from '@/lib/questionNumbering';
import { FileDropzone } from '@/components/FileDropzone';
import {
  cleanQuestionLabel, getQuestionType, getQuestionLabel,
  normaliseYesNoAnswer, normaliseNpsAnswer, normaliseNumberAnswer,
  formatSubmissionWindow, answerComment, shouldShowCommentBox,
  DISPLAY_ONLY_TYPES, CHOICE_TYPES, YES_NO_TYPES, RATING_TYPES, NPS_TYPES,
  NUMBER_TYPES, TEXT_TYPES,
} from '@/lib/answerFormatting';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '—'; }
}

function AudioPlayer({ url, label = 'Voice' }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const resolvedUrl = !url ? '' : (/^https?:/i.test(url) ? url : `${baseUrl}${url}`);

  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(resolvedUrl);
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => { setPlaying(false); toast.error('Could not load audio'); };
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  if (!url) return null;
  return (
    <button
      onClick={toggle}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
        playing ? 'bg-[#D62B27] text-white' : 'bg-[#D62B27]/10 text-[#D62B27] hover:bg-[#D62B27]/20'
      }`}
      data-testid={`qa-audio-${label}`}
    >
      {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      {playing ? 'Pause' : `Play ${label}`}
    </button>
  );
}

function asString(v) {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(x => (typeof x === 'object' && x && (x.label || x.text)) ? (x.label || x.text) : String(x)).join(', ');
  if (typeof v === 'object') {
    if (v.label) return v.label;
    if (v.text) return v.text;
    if ('value' in v) return asString(v.value);
    return '';
  }
  return String(v);
}

function prettyType(t) {
  if (!t) return 'Question';
  const map = {
    short_text: 'Short text', long_text: 'Long text', voice: 'Voice',
    yes_no: 'Yes / No', single_choice: 'Single choice', multiple_choice: 'Multiple choice',
    dropdown: 'Dropdown', button_choice: 'Choice',
    star_rating: 'Star rating', rating_scale: 'Rating',
    file_upload: 'File upload', date: 'Date', time: 'Time',
    number: 'Number', number_input: 'Number', nps: 'NPS®',
  };
  return map[t] || String(t).replace(/_/g, ' ');
}

function toAbsUrl(u) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (!u) return '';
  if (/^https?:/i.test(u)) return u;
  if (u.startsWith('/api/')) return base + u;
  return u;
}

function StarRow({ value, max = 5 }) {
  const n = Math.max(0, Math.min(max, Number(value) || 0));
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < n ? 'fill-[#F1D511] text-[#F1D511]' : 'text-[#E2E8F0]'}`} />
      ))}
      <span className="text-xs text-[#64748B] ml-1.5">{n}/{max}</span>
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    submitted:         { label: 'Submitted', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
    in_review:         { label: 'In Review', cls: 'bg-blue-100 text-blue-800 border border-blue-200' },
    awaiting_revision: { label: 'Awaiting Revision', cls: 'bg-purple-100 text-purple-800 border border-purple-200' },
    revision_requested:{ label: 'Revision Requested', cls: 'bg-purple-100 text-purple-800 border border-purple-200' },
    published:         { label: 'Published', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
    rejected:          { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border border-rose-200' },
  };
  const m = map[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-700 border border-gray-200' };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`} data-testid={`qa-detail-status-${status}`}>{m.label}</span>;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Resolve the list of option labels for a choice-type question. Builder
 * questions store options as either [{label}, …] or as a plain string[].
 * ──────────────────────────────────────────────────────────────────────── */
function resolveOptions(q) {
  const qtype = getQuestionType(q);
  if (qtype === 'yes_no') return [{ label: 'Yes' }, { label: 'No' }, { label: 'N/A' }];
  const opts = q.options || q.choices || [];
  return (Array.isArray(opts) ? opts : []).map(o => {
    if (typeof o === 'string') return { label: o };
    if (o && typeof o === 'object') return { label: o.label || o.text || String(o.value ?? '') };
    return null;
  }).filter(Boolean);
}

/* Extract the currently-selected label(s) from a stored choice answer. */
function selectedLabels(raw, qtype) {
  if (raw == null) return [];
  // Yes/No has special normalisation that handles every legacy shape.
  if (qtype === 'yes_no') {
    const n = normaliseYesNoAnswer(raw);
    return n ? [n] : [];
  }
  if (Array.isArray(raw)) {
    return raw.map(x => (x && typeof x === 'object') ? (x.label || x.text || String(x.value ?? '')) : String(x)).filter(Boolean);
  }
  if (typeof raw === 'object') {
    if (Array.isArray(raw.value)) {
      return raw.value.map(x => (x && typeof x === 'object') ? (x.label || x.text || String(x.value ?? '')) : String(x)).filter(Boolean);
    }
    return [raw.label || (raw.value != null ? String(raw.value) : asString(raw))].filter(Boolean);
  }
  return [String(raw)].filter(Boolean);
}

/* Build the qa_edits "edited_answer" payload for a selection edit. */
function buildSelectionEdit(qtype, selection) {
  // Yes/No → store as a structured wrapper that matches the original shape.
  if (qtype === 'yes_no') {
    const v = selection[0];
    if (!v) return null;
    const lower = String(v).toLowerCase();
    const map = { yes: { value: 'yes', score_value: 1 }, no: { value: 'no', score_value: 0 }, 'n/a': { value: 'n/a', score_value: null } };
    return { label: v, ...(map[lower] || { value: lower }) };
  }
  if (qtype === 'multiple_choice') {
    return selection.map(label => ({ label }));
  }
  // single_choice / dropdown / button_choice
  return { label: selection[0] || null };
}

/* ─── ONE editable answer block (text/comment debounce + flag toggle) ───── */
/* ─── Fix 3 + Fix 5 — QA File Manager (view / video / delete / add) ─────
 * Files are managed via the qa_edits[] overlay:
 *   - file_added entries:   QA-uploaded files (visible to client report too)
 *   - file_removed entries: hide a satisFINDER-uploaded file from client view
 * The underlying answers[<qid>] file list is NEVER mutated.
 * --------------------------------------------------------------------- */
function QAFileManager({ qid, rawFiles, qaEdits, responseId, branchId, allowed, maxSizeMb, maxFiles, onChange, isPlatformAdmin }) {
  const [removing, setRemoving] = useState(false);
  const [confirmRemoveUrl, setConfirmRemoveUrl] = useState(null);
  // In-flight / failed QA uploads (per-file progress + retry).
  const [transient, setTransient] = useState([]);

  const effMaxFiles = Math.max(1, Math.min(50, Number(maxFiles) || 1));
  const allowedList = (allowed && allowed.length) ? allowed : ['image', 'pdf', 'video'];
  const acceptAttr = [
    allowedList.includes('image') ? 'image/jpeg,image/png,image/webp' : '',
    allowedList.includes('pdf') ? 'application/pdf' : '',
    allowedList.includes('video') ? 'video/mp4,video/quicktime,video/webm' : '',
  ].filter(Boolean).join(',') || 'image/*';
  const allowedMimes = useMemo(() => new Set([
    ...(allowedList.includes('image') ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'] : []),
    ...(allowedList.includes('pdf') ? ['application/pdf'] : []),
    ...(allowedList.includes('video') ? ['video/mp4', 'video/quicktime', 'video/x-quicktime', 'video/webm'] : []),
  ]), [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  const removedUrls = useMemo(() => new Set(
    (qaEdits || [])
      .filter(e => e.edit_target === 'file' && e.action === 'file_removed' && e.question_id === qid)
      .map(e => e.file_url)
  ), [qaEdits, qid]);
  const qaAdded = useMemo(() => (qaEdits || [])
    .filter(e => e.edit_target === 'file' && e.action === 'file_added' && e.question_id === qid)
    .map(e => ({ url: e.file_url, name: e.filename, type: e.mime, comment: e.comment, _qaAdded: true, uploaded_by: e.uploaded_by, uploaded_at: e.uploaded_at })),
    [qaEdits, qid]);

  // Combined list — preserve order: original files first (filtered), then QA-added.
  const visibleFiles = [
    ...(rawFiles || []).map(f => ({ ...f, _removed: removedUrls.has(f.url) })),
    ...qaAdded,
  ];

  // Existing (non-removed) files + in-progress uploads count toward the limit.
  const pendingCount = transient.filter(t => t._pending).length;
  const activeCount = visibleFiles.filter(f => !f._removed).length + pendingCount;
  const remainingSlots = Math.max(0, effMaxFiles - activeCount);
  const anyUploading = pendingCount > 0;

  const detect = (f) => {
    const url = (f && f.url) || '';
    const t = (f && f.type) || '';
    if (t.startsWith('video/') || /\.(mp4|mov|webm)$/i.test(url)) return 'video';
    if (t.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(url)) return 'image';
    if (t === 'application/pdf' || /\.pdf$/i.test(url)) return 'pdf';
    return 'other';
  };

  // Upload to S3, then register on the FIND via qa_edits overlay.
  const uploadAndRegister = async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('branch_id', branchId || '');
    form.append('question_id', qid);
    form.append('allowed_types', allowedList.join(','));
    form.append('max_file_size_mb', String(maxSizeMb));
    const upRes = await fetch(`${API}/public/survey/upload-file`, { method: 'POST', body: form });
    if (!upRes.ok) {
      const err = await upRes.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    const upData = await upRes.json();
    const regRes = await fetch(`${API}/qa/finds/${responseId}/add-file`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question_id: qid,
        file_url: upData.file_url,
        name: file.name,
        mime: file.type,
        size_bytes: file.size,
      }),
    });
    if (!regRes.ok) {
      const err = await regRes.json().catch(() => ({}));
      throw new Error(err.detail || 'Could not register file');
    }
    return await regRes.json();
  };

  const onPickMulti = async (files) => {
    if (!files.length) return;
    // Reject the WHOLE batch if it exceeds the remaining allowance.
    if (files.length > remainingSlots) {
      toast.error(`You can upload ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'}.`);
      return;
    }
    for (const file of files) {
      if (allowedMimes.size > 0 && !allowedMimes.has((file.type || '').toLowerCase())) {
        toast.error(`${file.name}: file type not supported`);
        continue;
      }
      if (file.size > maxSizeMb * 1024 * 1024) {
        toast.error(`${file.name}: exceeds ${maxSizeMb}MB`);
        continue;
      }
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setTransient(prev => [...prev, { _key: key, _pending: true, _file: file, name: file.name, size: file.size }]);
      try {
        const regData = await uploadAndRegister(file);
        setTransient(prev => prev.filter(t => t._key !== key));
        onChange(regData.qa_edits);
      } catch (e) {
        setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: false, _error: e.message || 'Upload failed' } : t));
      }
    }
  };

  const retryTransient = async (key) => {
    const target = transient.find(t => t._key === key);
    if (!target) return;
    setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: true, _error: undefined } : t));
    try {
      const regData = await uploadAndRegister(target._file);
      setTransient(prev => prev.filter(t => t._key !== key));
      onChange(regData.qa_edits);
    } catch (e) {
      setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: false, _error: e.message || 'Upload failed' } : t));
    }
  };
  const removeTransient = (key) => setTransient(prev => prev.filter(t => t._key !== key));

  const onConfirmRemove = async () => {
    if (!confirmRemoveUrl) return;
    setRemoving(true);
    try {
      const res = await fetch(`${API}/qa/finds/${responseId}/remove-file`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: qid, file_url: confirmRemoveUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not remove file');
        return;
      }
      const data = await res.json();
      onChange(data.qa_edits);
      toast.success('File removed');
    } catch {
      toast.error('Could not remove file');
    } finally {
      setRemoving(false);
      setConfirmRemoveUrl(null);
    }
  };

  const hasAny = visibleFiles.length > 0 || transient.length > 0;

  return (
    <div className="space-y-2" data-testid={`qa-file-manager-${qid}`}>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">Uploaded files</span>
      {!hasAny && <p className="text-xs text-[#94A3B8] italic" data-testid={`qa-empty-files-${qid}`}>No files uploaded yet.</p>}
      {hasAny && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {visibleFiles.map((f, i) => {
            // Hide removed files unless platform_admin (audit visibility).
            if (f._removed && !isPlatformAdmin) return null;
            const url = toAbsUrl(f.url);
            const kind = detect(f);
            return (
              <div
                key={`${f.url}-${i}`}
                className={`relative border rounded-md p-2 space-y-1 ${f._removed ? 'border-rose-200 bg-rose-50 opacity-60' : 'border-[#E2E8F0] bg-white'}`}
                data-testid={`qa-file-${qid}-${i}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {f._qaAdded && <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-[9px] px-1.5 py-0">QA</Badge>}
                    {f._removed && <Badge className="bg-rose-100 text-rose-800 border border-rose-200 text-[9px] px-1.5 py-0">Removed by QA</Badge>}
                  </div>
                  {!f._removed && (
                    <button
                      type="button"
                      onClick={() => setConfirmRemoveUrl(f.url)}
                      className="text-rose-600 hover:bg-rose-100 rounded p-0.5"
                      aria-label="Remove file"
                      data-testid={`qa-file-remove-${qid}-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {kind === 'image' ? (
                  <a href={url} target="_blank" rel="noreferrer" className="block">
                    <img src={url} alt={f.name || `file-${i + 1}`} className="w-full h-[120px] object-cover rounded" style={{ height: 120 }} />
                  </a>
                ) : kind === 'video' ? (
                  <video controls src={url} className="w-full rounded bg-black" style={{ maxHeight: 200 }} data-testid={`qa-video-${qid}-${i}`} preload="metadata">
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center bg-[#F8FAFC] rounded text-[#64748B] hover:text-[#1A3E6F]" style={{ height: 120 }}>
                    <FileText className="w-8 h-8" />
                    <span className="text-[10px] mt-1 uppercase">{kind === 'pdf' ? 'PDF' : 'File'}</span>
                  </a>
                )}
                <p className="text-[11px] text-[#475569] truncate" title={f.name}>{f.name || 'file'}</p>
                {f.comment && (
                  <div
                    className="mt-2 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2"
                    data-testid={`qa-file-comment-${qid}-${i}`}
                  >
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B] mb-1">
                      Comment
                    </p>
                    <p className="text-sm text-[#0F172A] whitespace-pre-wrap break-words">
                      {f.comment}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
          {transient.map((t) => (
            <div
              key={t._key}
              className={`relative border rounded-md p-2 space-y-1 flex flex-col items-center justify-center text-center ${t._error ? 'border-rose-300 bg-rose-50' : 'border-blue-200 bg-blue-50'}`}
              style={{ minHeight: 140 }}
              data-testid={`qa-file-transient-${qid}-${t._key}`}
            >
              {t._pending ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin text-[#1A3E6F]" />
                  <p className="text-[11px] text-[#1A3E6F] mt-1">Uploading…</p>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-6 h-6 text-rose-600" />
                  <p className="text-[11px] text-rose-700 mt-1">Upload failed</p>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => retryTransient(t._key)} className="text-[11px] text-[#1A3E6F] underline font-semibold" data-testid={`qa-file-retry-${qid}-${t._key}`}>Retry</button>
                    <button type="button" onClick={() => removeTransient(t._key)} className="text-[11px] text-[#64748B] underline">Dismiss</button>
                  </div>
                </>
              )}
              <p className="text-[10px] text-[#94A3B8] truncate w-full px-1" title={t.name}>{t.name}</p>
            </div>
          ))}
        </div>
      )}
      {/* Add file(s) — multi-select + drag and drop */}
      {remainingSlots > 0 ? (
        <div className="pt-2">
          <FileDropzone
            onFiles={onPickMulti}
            accept={acceptAttr}
            multiple={effMaxFiles > 1}
            disabled={anyUploading}
            idleLabel={anyUploading ? 'Uploading…' : (activeCount === 0 ? `Add files (${maxSizeMb}MB max)` : `Add more (${maxSizeMb}MB max)`)}
            subLabel={effMaxFiles > 1 ? `— ${activeCount}/${effMaxFiles}` : ''}
            inputTestId={`qa-file-input-${qid}`}
            dropTestId={`qa-file-add-${qid}`}
          />
        </div>
      ) : (
        <p className="pt-2 text-xs text-[#64748B] italic" data-testid={`qa-file-limit-${qid}`}>Maximum {effMaxFiles} file{effMaxFiles === 1 ? '' : 's'} reached</p>
      )}
      {/* Confirmation modal */}
      {confirmRemoveUrl && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid={`qa-file-remove-confirm-${qid}`}>
          <div className="bg-white rounded-lg max-w-sm w-full p-5 space-y-3">
            <p className="font-semibold text-[#0F172A]">Remove this file from the FIND?</p>
            <p className="text-xs text-[#64748B]">The file will be hidden from the client report. The original satisFINDER submission is preserved in the audit trail.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmRemoveUrl(null)} disabled={removing} data-testid={`qa-file-remove-cancel-${qid}`}>Cancel</Button>
              <Button size="sm" onClick={onConfirmRemove} disabled={removing} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`qa-file-remove-confirm-yes-${qid}`}>
                {removing ? 'Removing…' : 'Yes, remove'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── Platform-admin only: Add missing answer ─────────────────────────
 * Exception/correction tool for filling in an answer that was never
 * captured on submission (e.g. earlier survey/config bug). This is NOT
 * the normal satisFINDER revision path. Only platform_admin sees the
 * "+ Add answer" affordance; the underlying `/add-missing-answer`
 * endpoint also enforces platform-admin-only server-side.
 *
 * Question type → control mapping:
 *   short/long/voice/file_upload → textarea (string)
 *   yes_no                       → Yes / No pills → {label, value, score_value}
 *   single_choice / dropdown /
 *   button_choice                → pill picker (options from resolveOptions)
 *                                  → {label}
 *   multiple_choice              → checkboxes → [{label}, …]
 *   star_rating                  → number 0..scale_max
 *   nps                          → 0..10 pill picker (int)
 *   number                       → numeric input
 *   date / time                  → native date/time input
 *
 * On save, POSTs to /qa/finds/{id}/add-missing-answer. The endpoint
 * refuses to overwrite (409) if an answer already exists, and returns
 * the fresh `answers`, `qa_edits`, `nps_score` slice so the parent can
 * merge without a full reload. The audit entry lands in qa_edits with
 * action="missing_answer_added".
 */
function AddMissingAnswerControl({ q, qid, qtype, responseId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(() => {
    if (qtype === 'multiple_choice') return [];
    if (NUMBER_TYPES.has(qtype) || RATING_TYPES.has(qtype) || NPS_TYPES.has(qtype)) return '';
    return '';
  });

  // Value validity check → drives Save enablement.
  const canSave = (() => {
    if (saving) return false;
    if (qtype === 'multiple_choice') return Array.isArray(value) && value.length > 0;
    if (NPS_TYPES.has(qtype)) {
      const n = Number(value);
      return value !== '' && Number.isFinite(n) && n >= 0 && n <= 10;
    }
    if (RATING_TYPES.has(qtype)) {
      const n = Number(value);
      const max = Number(q.scale_max) || 5;
      return value !== '' && Number.isFinite(n) && n >= 0 && n <= max;
    }
    if (NUMBER_TYPES.has(qtype)) {
      return value !== '' && Number.isFinite(Number(value));
    }
    if (typeof value === 'string') return value.trim().length > 0;
    return value != null;
  })();

  // Shape the value into what the API/answers dict expects, mirroring
  // how a fresh submission would store it.
  const shapedValue = () => {
    if (qtype === 'yes_no') return buildSelectionEdit('yes_no', [value]);
    if (qtype === 'multiple_choice') return buildSelectionEdit('multiple_choice', value);
    if (['single_choice', 'dropdown', 'button_choice'].includes(qtype)) {
      return buildSelectionEdit(qtype, [value]);
    }
    if (NPS_TYPES.has(qtype)) return Number(value);
    if (RATING_TYPES.has(qtype)) return Number(value);
    if (NUMBER_TYPES.has(qtype)) return Number(value);
    if (['date', 'time'].includes(qtype)) return String(value);
    return String(value); // text / voice / file_upload
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/qa/finds/${responseId}/add-missing-answer`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: qid, value: shapedValue() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || `Failed to add answer (${res.status})`);
        return;
      }
      const data = await res.json();
      toast.success('Missing answer added.');
      setOpen(false);
      onAdded(data);
    } catch (e) {
      toast.error('Network error adding missing answer.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <div className="flex items-center gap-2" data-testid={`qa-missing-add-row-${qid}`}>
        <span className="text-sm font-medium text-[#94A3B8]" data-testid={`qa-missing-dash-${qid}`}>—</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpen(true)}
          className="h-7 px-2 text-xs text-[#1A3E6F] hover:bg-[#1A3E6F]/5"
          data-testid={`qa-add-missing-btn-${qid}`}
        >
          <Plus className="w-3 h-3 mr-1" /> Add answer
        </Button>
        <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Platform admin</span>
      </div>
    );
  }

  const options = CHOICE_TYPES.has(qtype) ? resolveOptions(q) : [];
  const scaleMax = Number(q.scale_max) || 5;

  return (
    <div
      className="bg-amber-50/40 border border-amber-200 rounded-lg p-3 space-y-2"
      data-testid={`qa-add-missing-editor-${qid}`}
    >
      <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">
        Add missing answer · Platform admin
      </p>

      {/* Text / voice / file_upload — textarea */}
      {(TEXT_TYPES.has(qtype) || qtype === 'voice' || qtype === 'file_upload') && (
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={5}
          placeholder="Type the missing answer"
          disabled={saving}
          className="resize-y bg-white"
          data-testid={`qa-add-missing-text-${qid}`}
        />
      )}

      {/* Yes/No pills */}
      {qtype === 'yes_no' && (
        <div className="flex flex-wrap gap-2">
          {['Yes', 'No'].map(l => (
            <button
              key={l}
              type="button"
              onClick={() => setValue(l)}
              disabled={saving}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                value === l ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#1A3E6F]'
              }`}
              data-testid={`qa-add-missing-yesno-${qid}-${l}`}
            >{l}</button>
          ))}
        </div>
      )}

      {/* Single choice / dropdown / button_choice — pills */}
      {['single_choice', 'dropdown', 'button_choice'].includes(qtype) && (
        <div className="flex flex-wrap gap-2">
          {options.map(o => (
            <button
              key={o.label}
              type="button"
              onClick={() => setValue(o.label)}
              disabled={saving}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                value === o.label ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#1A3E6F]'
              }`}
              data-testid={`qa-add-missing-choice-${qid}-${o.label}`}
            >{o.label}</button>
          ))}
        </div>
      )}

      {/* Multiple choice — checkboxes */}
      {qtype === 'multiple_choice' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {options.map(o => {
            const checked = Array.isArray(value) && value.includes(o.label);
            return (
              <label
                key={o.label}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E2E8F0] cursor-pointer hover:border-[#1A3E6F] bg-white"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => {
                    setValue(prev => {
                      const s = new Set(Array.isArray(prev) ? prev : []);
                      if (v) s.add(o.label); else s.delete(o.label);
                      return Array.from(s);
                    });
                  }}
                  disabled={saving}
                  data-testid={`qa-add-missing-multi-${qid}-${o.label}`}
                />
                <span className="text-sm text-[#0F172A]">{o.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* NPS 0..10 pills */}
      {NPS_TYPES.has(qtype) && (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 11 }, (_, i) => i).map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setValue(String(n))}
              disabled={saving}
              className={`w-9 h-9 rounded-full text-sm font-bold border transition-colors ${
                String(value) === String(n)
                  ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                  : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#1A3E6F]'
              }`}
              data-testid={`qa-add-missing-nps-${qid}-${n}`}
            >{n}</button>
          ))}
        </div>
      )}

      {/* Rating — number input 0..scale_max */}
      {RATING_TYPES.has(qtype) && (
        <input
          type="number"
          min={0}
          max={scaleMax}
          step="1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          className="text-sm font-medium bg-white border border-[#CBD5E1] rounded px-2.5 py-1 max-w-[140px]"
          placeholder={`0 – ${scaleMax}`}
          data-testid={`qa-add-missing-rating-${qid}`}
        />
      )}

      {/* Number */}
      {NUMBER_TYPES.has(qtype) && (
        <input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          className="text-sm font-medium bg-white border border-[#CBD5E1] rounded px-2.5 py-1 max-w-[160px]"
          data-testid={`qa-add-missing-number-${qid}`}
        />
      )}

      {/* Date / Time */}
      {['date', 'time'].includes(qtype) && (
        <input
          type={qtype}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          className="text-sm font-medium bg-white border border-[#CBD5E1] rounded px-2.5 py-1"
          data-testid={`qa-add-missing-${qtype}-${qid}`}
        />
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          onClick={save}
          disabled={!canSave}
          className="h-7 px-3 text-xs bg-[#1A3E6F] hover:bg-[#0F2A4F] text-white"
          data-testid={`qa-add-missing-save-${qid}`}
        >{saving ? 'Saving…' : 'Save'}</Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => { setOpen(false); }}
          disabled={saving}
          className="h-7 px-3 text-xs"
          data-testid={`qa-add-missing-cancel-${qid}`}
        >Cancel</Button>
      </div>
    </div>
  );
}


function AnswerBlock({
  q, number, answers, qaEdits, revisionFlags, onSaveTextEdit, onSaveSelectionEdit, onToggleFlag, busy,
  find, responseId, user, setFind,
}) {
  const qid = q.question_id || q.id;
  const qtype = getQuestionType(q);
  const qtext = getQuestionLabel(q); // strips HTML + (copy) artefacts
  const raw = answers?.[qid];

  // Voice support
  const isVoice = raw && typeof raw === 'object' && !Array.isArray(raw) && (raw.audio_url || raw.transcription != null);
  const voiceUrl = isVoice ? raw.audio_url : null;

  // Existing QA edit (if any)
  const existingEdit = (qaEdits || []).find(e => e.question_id === qid);
  const isSelectionEdited = existingEdit?.edit_target === 'selection';

  // Decide editable target
  let editTarget = 'none'; // 'text' | 'comment' | 'selection' | 'none'
  let baseText = '';
  if (TEXT_TYPES.has(qtype) || isVoice) {
    editTarget = 'text';
    baseText = isVoice ? (raw?.transcription || '') : (typeof raw === 'string' ? raw : asString(raw));
  } else if (CHOICE_TYPES.has(qtype)) {
    editTarget = 'selection';
    baseText = (raw && typeof raw === 'object' && typeof raw.comment === 'string') ? raw.comment : '';
  } else if (RATING_TYPES.has(qtype)) {
    editTarget = 'comment';
    baseText = (raw && typeof raw === 'object' && typeof raw.comment === 'string') ? raw.comment : '';
  } else if (NPS_TYPES.has(qtype)) {
    // NPS is read-only in QA. The score is displayed above and its
    // follow-up reason belongs in the separate survey question.
    editTarget = 'none';
    baseText = '';
  }

  // ── Text/comment debounce wiring ──
  // For CHOICE types (yes_no, single_choice, dropdown, multiple_choice) editTarget
  // is 'selection' but the question can ALSO carry a free-text comment from the
  // satisFINDER. We must seed the textarea with that existing comment, otherwise
  // QA sees an empty box and the comment disappears from view.
  // The existing edit overlay only fills the textarea when it was a TEXT/COMMENT
  // edit — selection edits leave the comment baseline untouched.
  const initialText = (existingEdit && !isSelectionEdited && existingEdit.edit_target !== 'selection')
    ? (existingEdit.edited_answer ?? '')
    : baseText;
  const [text, setText] = useState(initialText);
  const [savedText, setSavedText] = useState(initialText);
  const [savingState, setSavingState] = useState('idle');
  const debounceRef = useRef(null);

  useEffect(() => {
    setText(initialText);
    setSavedText(initialText);
    setSavingState('idle');
    // eslint-disable-next-line
  }, [qid, existingEdit?.edited_at, baseText]);

  const flag = (revisionFlags || []).find(f => f.question_id === qid && !f.resolved);
  const [flagged, setFlagged] = useState(!!flag);
  const [note, setNote] = useState(flag?.qa_note || '');
  useEffect(() => {
    setFlagged(!!flag);
    setNote(flag?.qa_note || '');
    // eslint-disable-next-line
  }, [flag?.flagged_at, flag?.qa_note]);

  const onTextChange = (v) => {
    setText(v);
    setSavingState('typing');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (v === savedText) { setSavingState('idle'); return; }
      setSavingState('saving');
      const ok = await onSaveTextEdit(qid, v);
      if (ok) { setSavedText(v); setSavingState('saved'); setTimeout(() => setSavingState('idle'), 1500); }
      else { setSavingState('idle'); }
    }, 2000);
  };
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const flagToggle = async (next) => { setFlagged(next); await onToggleFlag(qid, next, note); };
  const flagNoteBlur = async () => { if (flagged) await onToggleFlag(qid, true, note); };

  // ── Selection display: overlay qa_edits selection edits onto raw ──
  const effectiveRaw = isSelectionEdited ? existingEdit.edited_answer : raw;
  const currentLabels = CHOICE_TYPES.has(qtype) ? selectedLabels(effectiveRaw, qtype) : [];

  // ── File upload list ──
  const fileList = (() => {
    if (qtype !== 'file_upload') return null;
    if (!Array.isArray(raw)) return null;
    return raw.filter(f => f && typeof f === 'object' && typeof f.url === 'string');
  })();

  // ── Empty detection ──
  const hasAnyAnswer = (() => {
    if (raw == null) return false;
    if (typeof raw === 'string') return raw.trim().length > 0;
    if (Array.isArray(raw)) return raw.length > 0;
    if (typeof raw === 'object') return Object.keys(raw).length > 0;
    return true;
  })();

  // ── Missing-answer detection (platform_admin gets the "+ Add answer" UI) ──
  const isMissing = !hasAnyAnswer && !existingEdit;
  const isPlatformAdmin = user?.role === 'platform_admin' || user?.role === 'super_admin';
  // Merger for AddMissingAnswerControl → parent `find` state.
  const onMissingAdded = (payload) => {
    setFind(prev => prev ? ({
      ...prev,
      answers: payload.answers ?? prev.answers,
      qa_edits: payload.qa_edits ?? prev.qa_edits,
      ...(payload.nps_score !== undefined && payload.nps_score !== null
        ? { nps_score: payload.nps_score } : {}),
    }) : prev);
  };

  // ── Star rating value ──
  const ratingValue = (() => {
    if (!RATING_TYPES.has(qtype)) return null;
    if (raw == null) return null;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string' && raw.match(/^-?\d+(\.\d+)?$/)) return Number(raw);
    if (typeof raw === 'object') {
      if (typeof raw.value === 'number') return raw.value;
      if (typeof raw.score_value === 'number') return raw.score_value;
      if (typeof raw.value === 'string' && raw.value.match(/^-?\d+$/)) return Number(raw.value);
    }
    return null;
  })();

  // ── NPS value ──
  const npsValue = NPS_TYPES.has(qtype) ? normaliseNpsAnswer(raw) : null;
  // ── Number value ──
  const numberValue = NUMBER_TYPES.has(qtype) ? normaliseNumberAnswer(raw) : null;

  // ── DISPLAY-ONLY blocks (welcome / section_header / etc.) ──
  // Section headers act as VISUAL DIVIDERS only — no number, no flag toggle.
  if (DISPLAY_ONLY_TYPES.has(qtype)) {
    const sub = q.sub_text || q.help_text || q.subtitle || q.description || '';
    const cleanSub = sub ? cleanQuestionLabel(sub) : '';
    // Section header gets a stronger visual treatment
    if (qtype === 'section_header') {
      return (
        <div className="pt-4 pb-2" data-testid={`qa-section-header-${qid}`}>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3E6F]">{number ? `Section ${number}` : 'Section'}</span>
            <div className="flex-1 h-px bg-[#1A3E6F]/20" />
          </div>
          {qtext && <h3 className="font-['Montserrat'] text-lg font-bold text-[#0F172A] mt-2">{qtext}</h3>}
          {cleanSub && <p className="text-sm text-[#64748B] mt-0.5">{cleanSub}</p>}
        </div>
      );
    }
    // Welcome / thank you / rich_text / image / page break / confirmation
    return (
      <div className="bg-[#F8FAFC] border-l-2 border-[#CBD5E1] rounded-r-md px-4 py-3 space-y-0.5" data-testid={`qa-display-block-${qid}`}>
        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">
          {prettyType(qtype)}
        </p>
        {qtext ? <p className="text-sm font-semibold text-[#475569]">{qtext}</p> : <p className="text-xs text-[#94A3B8] italic">(no title set in survey builder)</p>}
        {cleanSub && <p className="text-xs text-[#64748B]">{cleanSub}</p>}
      </div>
    );
  }

  // ── Options for selection editor ──
  const options = CHOICE_TYPES.has(qtype) ? resolveOptions(q) : [];
  const isMulti = qtype === 'multiple_choice';

  // Selection editors
  const onPickSingle = async (label) => {
    await onSaveSelectionEdit(qid, buildSelectionEdit(qtype, [label]));
  };
  const onToggleMulti = async (label, checked) => {
    const current = new Set(currentLabels);
    if (checked) current.add(label); else current.delete(label);
    const arr = Array.from(current);
    await onSaveSelectionEdit(qid, buildSelectionEdit(qtype, arr));
  };

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3" data-testid={`qa-answer-block-${qid}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-[#94A3B8] font-semibold">{number ? `${number}.` : ''}</span>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#64748B] bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-2 py-0.5">
              {qtype === 'voice' || isVoice ? <Volume2 className="w-3 h-3 text-[#D62B27]" /> : null}
              {qtype === 'file_upload' ? <ImageIcon className="w-3 h-3" /> : null}
              {RATING_TYPES.has(qtype) ? <Star className="w-3 h-3 text-[#F1D511]" /> : null}
              {qtype === 'multiple_choice' ? <ListChecks className="w-3 h-3" /> : null}
              {(CHOICE_TYPES.has(qtype) && qtype !== 'multiple_choice') ? <CheckSquare className="w-3 h-3" /> : null}
              {qtype === 'date' ? <Calendar className="w-3 h-3" /> : null}
              {NUMBER_TYPES.has(qtype) ? <Hash className="w-3 h-3" /> : null}
              {NPS_TYPES.has(qtype) ? <span className="font-bold">NPS</span> : null}
              {prettyType(qtype)}
            </span>
            {q.is_required && <span className="text-[10px] text-rose-600 font-semibold">required</span>}
            {/* Part B2: TrustX® dimension pill */}
            {q.qualifier_id && (
              <QualifierPill qualifierId={q.qualifier_id} qualifiers={find?.survey_qualifiers || []} />
            )}
          </div>
          <p className="font-medium text-[#0F172A] text-sm leading-snug">
            {qtext || <span className="text-[#94A3B8] italic">Untitled question</span>}
          </p>
          {/* Platform-admin only — question_id + synthetic answer_id for
              debugging / audit-trail tracing. Hidden for QA / tenant_admin /
              client roles. */}
          <AdminIdRow
            questionId={qid}
            responseId={responseId}
            className="mt-0.5"
            testId={`qa-admin-id-${qid}`}
          />
          {(q.sub_text || q.help_text || q.subtitle || q.description) && (
            <p className="text-xs text-[#64748B] mt-0.5">
              {cleanQuestionLabel(q.sub_text || q.help_text || q.subtitle || q.description)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {savingState === 'typing' && <span className="text-xs text-[#94A3B8]" data-testid={`qa-save-state-${qid}-typing`}>Typing…</span>}
          {savingState === 'saving' && <span className="text-xs text-[#1A3E6F] inline-flex items-center gap-1" data-testid={`qa-save-state-${qid}-saving`}><Save className="w-3 h-3 animate-pulse" /> Saving…</span>}
          {savingState === 'saved'  && <span className="text-xs text-emerald-600 inline-flex items-center gap-1" data-testid={`qa-save-state-${qid}-saved`}><CheckCircle2 className="w-3 h-3" /> Saved</span>}
          {existingEdit && (
            <Badge
              className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]"
              data-testid={`qa-edited-badge-${qid}`}
              title={`Edited by ${existingEdit.edited_by || 'QA'} on ${fmtDate(existingEdit.edited_at)}`}
            >
              QA edited
            </Badge>
          )}
        </div>
      </div>

      {/* Voice player */}
      {voiceUrl && (
        <div data-testid={`qa-voice-row-${qid}`}>
          <AudioPlayer url={voiceUrl} label={`Q${number || ''}`} />
        </div>
      )}

      {/* Yes/No / single_choice / dropdown — pill picker */}
      {CHOICE_TYPES.has(qtype) && !isMulti && !isMissing && (
        <div className="space-y-2" data-testid={`qa-selection-${qid}`}>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">Selection</span>
          {qtype === 'dropdown' ? (
            <Select
              value={currentLabels[0] || ''}
              onValueChange={onPickSingle}
              disabled={busy}
            >
              <SelectTrigger className="w-full max-w-xs" data-testid={`qa-select-${qid}`}>
                <SelectValue placeholder="Select an answer" />
              </SelectTrigger>
              <SelectContent>
                {options.map(o => (
                  <SelectItem key={o.label} value={o.label} data-testid={`qa-select-${qid}-${o.label}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex flex-wrap gap-2">
              {options.map(o => {
                const active = currentLabels[0] === o.label;
                return (
                  <button
                    key={o.label}
                    type="button"
                    onClick={() => !busy && onPickSingle(o.label)}
                    disabled={busy}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors disabled:cursor-not-allowed ${
                      active
                        ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                        : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#1A3E6F] hover:text-[#1A3E6F]'
                    }`}
                    data-testid={`qa-choice-${qid}-${o.label}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}
          {isSelectionEdited && (
            <p className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid={`qa-selection-edited-${qid}`}>
              <History className="w-3 h-3" />
              QA-edited selection (original satisFINDER answer preserved in audit trail)
            </p>
          )}
        </div>
      )}

      {/* Multiple choice — checkbox grid */}
      {isMulti && !isMissing && (
        <div className="space-y-2" data-testid={`qa-multi-${qid}`}>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">Selection (multiple)</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {options.map(o => {
              const checked = currentLabels.includes(o.label);
              return (
                <label
                  key={o.label}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E2E8F0] cursor-pointer hover:border-[#1A3E6F]"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => onToggleMulti(o.label, !!v)}
                    disabled={busy}
                    data-testid={`qa-multi-${qid}-${o.label}`}
                  />
                  <span className="text-sm text-[#0F172A]">{o.label}</span>
                </label>
              );
            })}
          </div>
          {isSelectionEdited && (
            <p className="text-[11px] text-amber-700 inline-flex items-center gap-1">
              <History className="w-3 h-3" />
              QA-edited selection (original satisFINDER answer preserved in audit trail)
            </p>
          )}
        </div>
      )}

      {/* Star rating display */}
      {RATING_TYPES.has(qtype) && ratingValue != null && (
        <div className="flex items-center gap-2" data-testid={`qa-rating-${qid}`}>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">Rating</span>
          <StarRow value={ratingValue} max={q.scale_max || 5} />
        </div>
      )}

      {/* NPS display */}
      {NPS_TYPES.has(qtype) && (
        <div className="flex items-center gap-2" data-testid={`qa-nps-${qid}`}>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">NPS® Score</span>
          {npsValue != null ? (
            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-base ${
              npsValue >= 9 ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
              : npsValue >= 7 ? 'bg-amber-100 text-amber-700 border border-amber-300'
              : 'bg-rose-100 text-rose-700 border border-rose-300'
            }`}>{npsValue}</span>
          ) : <span className="text-xs text-[#94A3B8] italic">No score</span>}
          <span className="text-[11px] text-[#64748B]">{npsValue == null ? '' : npsValue >= 9 ? 'Promoter' : npsValue >= 7 ? 'Passive' : 'Detractor'}</span>
        </div>
      )}

      {/* Number — Fix 2: editable (uses effectiveRaw so QA edits round-trip on reload) */}
      {NUMBER_TYPES.has(qtype) && !isMissing && (() => {
        const effNum = isSelectionEdited
          ? (typeof existingEdit.edited_answer === 'number' ? existingEdit.edited_answer : Number(existingEdit.edited_answer))
          : numberValue;
        const effNumStr = effNum == null || Number.isNaN(effNum) ? '' : String(effNum);
        return (
          <div className="flex items-center gap-2 flex-wrap" data-testid={`qa-number-${qid}`}>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">Number</span>
            <input
              key={`num-${qid}-${effNumStr}`}
              type="number"
              defaultValue={effNumStr}
              onBlur={(e) => {
                const v = e.target.value === '' ? null : Number(e.target.value);
                if (v !== effNum) onSaveSelectionEdit(qid, v);
              }}
              disabled={busy}
              data-testid={`qa-number-input-${qid}`}
              className="text-sm font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded px-2.5 py-1 max-w-[140px] focus:border-[#1A3E6F] focus:outline-none"
            />
            {isSelectionEdited && (
              <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                <History className="w-3 h-3" /> QA-edited
              </span>
            )}
          </div>
        );
      })()}

      {/* File upload — Fix 3 + Fix 5: QA file manager with video, delete, add */}
      {qtype === 'file_upload' && (
        <QAFileManager
          qid={qid}
          rawFiles={fileList || []}
          qaEdits={qaEdits || []}
          responseId={find?.response_id || responseId}
          branchId={find?.branch_id}
          allowed={(q.file_settings && q.file_settings.allowed_types) || ['image', 'pdf', 'video']}
          maxSizeMb={Number((q.file_settings && q.file_settings.max_file_size_mb) || 20)}
          maxFiles={Number((q.file_settings && q.file_settings.max_files) || 1)}
          onChange={(qaEditsNext) => setFind(prev => prev ? ({ ...prev, qa_edits: qaEditsNext }) : prev)}
          isPlatformAdmin={user?.role === 'platform_admin' || user?.role === 'super_admin'}
        />
      )}

      {/* Date / Time — Fix 2: editable (uses effectiveRaw for round-trip) */}
      {['date', 'time'].includes(qtype) && !isMissing && (() => {
        const effStr = asString(effectiveRaw) || '';
        return (
          <div className="flex items-center gap-2 flex-wrap" data-testid={`qa-datetime-${qid}`}>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">{qtype === 'date' ? 'Date' : 'Time'}</span>
            <input
              key={`${qtype}-${qid}-${effStr}`}
              type={qtype}
              defaultValue={effStr}
              onBlur={(e) => {
                const v = e.target.value || null;
                if (v !== effStr) onSaveSelectionEdit(qid, v);
              }}
              disabled={busy}
              data-testid={`qa-${qtype}-input-${qid}`}
              className="text-sm font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded px-2.5 py-1 focus:border-[#1A3E6F] focus:outline-none"
            />
            {isSelectionEdited && (
              <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                <History className="w-3 h-3" /> QA-edited
              </span>
            )}
          </div>
        );
      })()}

      {/* Editable text (only when there IS an answer/edit — otherwise the
          missing-answer control below handles it) */}
      {editTarget === 'text' && !isMissing && (
        <Textarea
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={isVoice ? 'Voice transcription — edit verbatim if needed' : 'Edit the verbatim answer'}
          rows={Math.min(14, Math.max(5, Math.ceil((text || '').length / 80)))}
          disabled={busy}
          data-testid={`qa-answer-input-${qid}`}
          className="resize-y"
        />
      )}

      {/* Missing-answer row — platform_admin sees "+ Add answer"; normal QA
          sees "-" only (Feb 2026). Applies to every question type. */}
      {isMissing && (
        isPlatformAdmin ? (
          <AddMissingAnswerControl
            q={q}
            qid={qid}
            qtype={qtype}
            responseId={responseId}
            onAdded={onMissingAdded}
          />
        ) : (
          <p
            className="text-sm font-medium text-[#94A3B8]"
            data-testid={`qa-missing-dash-${qid}`}
          >—</p>
        )
      )}

      {/* Editable comment (rating / NPS / commentable choice questions).
          Visibility is controlled by `shouldShowCommentBox` — for CHOICE
          questions we now consult the live `q.commentable` flag, which
          fixes older responses whose answer object predates the flag. */}
      {shouldShowCommentBox({ editTarget, qtype, q, raw, baseText }) && (
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8]">Comment</span>
          <Textarea
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Edit the satisFINDER's comment"
            rows={Math.min(14, Math.max(5, Math.ceil((text || '').length / 80)))}
            disabled={busy}
            data-testid={`qa-comment-input-${qid}`}
            className="resize-y"
          />
        </div>
      )}

      {/* History strip */}
      {/* Part A6 — satisFINDER revision strip: shown when the satisFINDER has
          re-submitted an answer for this question. Renders side-by-side as
          Original (strikethrough) → QA edit (amber) → Revised (green). */}
      {(() => {
        const revFlags = (revisionFlags || []).filter(
          f => f.question_id === qid && f.revised_answer !== undefined && f.revised_answer !== null
        );
        if (revFlags.length === 0) return null;
        return revFlags.map((rf, i) => (
          <div
            key={`rev-${i}`}
            className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 space-y-2"
            data-testid={`qa-revision-strip-${qid}-${rf.round || 1}`}
          >
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-emerald-800">
              <CheckCircle2 className="w-3.5 h-3.5" />
              satisFINDER<sup>®</sup> revision · Round {rf.round || 1}
              {rf.revised_at && <span className="text-emerald-700 font-normal normal-case tracking-normal ml-2">{fmtDate(rf.revised_at)}</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="bg-white border border-[#E2E8F0] rounded p-2">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8] mb-1">Original answer</p>
                <p className="text-xs text-[#64748B] line-through">
                  {CHOICE_TYPES.has(qtype)
                    ? (selectedLabels(raw, qtype).join(', ') || <em>(empty)</em>)
                    : (asString(raw) || <em>(empty)</em>)}
                </p>
              </div>
              <div className={`border rounded p-2 ${existingEdit ? 'bg-amber-50 border-amber-200' : 'bg-[#F8FAFC] border-dashed border-[#E2E8F0]'}`}>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 mb-1">QA edit</p>
                {existingEdit ? (
                  <p className="text-xs text-amber-900">
                    {isSelectionEdited
                      ? (selectedLabels(existingEdit.edited_answer, qtype).join(', ') || <em>(empty)</em>)
                      : (asString(existingEdit.edited_answer) || <em>(empty)</em>)}
                  </p>
                ) : (
                  <p className="text-xs text-[#94A3B8] italic">(no QA edit)</p>
                )}
              </div>
              <div className="bg-emerald-100/60 border border-emerald-300 rounded p-2">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800 mb-1">Revised by satisFINDER<sup>®</sup></p>
                <p className="text-xs text-emerald-900 whitespace-pre-wrap">
                  {CHOICE_TYPES.has(qtype)
                    ? (selectedLabels(rf.revised_answer, qtype).join(', ') || <em>(empty)</em>)
                    : (asString(rf.revised_answer) || <em>(empty)</em>)}
                </p>
              </div>
            </div>
            {rf.qa_note && (
              <p className="text-[11px] text-[#64748B] italic">QA note that prompted the revision: “{rf.qa_note}”</p>
            )}
          </div>
        ));
      })()}

      {existingEdit && existingEdit.original_answer != null && !isSelectionEdited && (
        <div className="text-[11px] text-[#64748B] bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded p-2 space-y-1" data-testid={`qa-history-${qid}`}>
          <div className="flex items-start gap-2">
            <History className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              <span className="font-semibold">Original{editTarget === 'comment' ? ' comment' : ''}:</span>{' '}
              {asString(existingEdit.original_answer) || <em className="text-[#94A3B8]">(empty)</em>}
            </span>
          </div>
          {existingEdit.edited_by && (
            <AdminEditMeta
              editedBy={existingEdit.edited_by}
              editedAt={existingEdit.edited_at}
              fmt={fmtDate}
              testId={`qa-history-edit-meta-${qid}`}
            />
          )}
          {/* Platform-admin only — IDs of the question + audit-trail row. */}
          <div className="pl-5">
            <AdminIdRow
              questionId={qid}
              responseId={responseId}
              testId={`qa-history-ids-${qid}`}
            />
          </div>
        </div>
      )}
      {isSelectionEdited && existingEdit?.original_answer != null && (
        <div className="text-[11px] text-[#64748B] bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded p-2 space-y-1" data-testid={`qa-history-${qid}`}>
          <div className="flex items-start gap-2">
            <History className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              <span className="font-semibold">Original selection:</span>{' '}
              {selectedLabels(existingEdit.original_answer, qtype).join(', ') || <em className="text-[#94A3B8]">(empty)</em>}
            </span>
          </div>
          {existingEdit.edited_by && (
            <AdminEditMeta
              editedBy={existingEdit.edited_by}
              editedAt={existingEdit.edited_at}
              fmt={fmtDate}
              testId={`qa-history-edit-meta-${qid}`}
            />
          )}
          {/* Platform-admin only — IDs of the question + audit-trail row. */}
          <div className="pl-5">
            <AdminIdRow
              questionId={qid}
              responseId={responseId}
              testId={`qa-history-ids-${qid}`}
            />
          </div>
        </div>
      )}

      {/* Flag for revision (visible for every non-display-only question) */}
      <div className="pt-2 border-t border-dashed border-[#E2E8F0] space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch checked={flagged} onCheckedChange={flagToggle} data-testid={`qa-flag-toggle-${qid}`} />
          <span className="text-xs font-medium text-[#475569] inline-flex items-center gap-1">
            <Flag className={`w-3.5 h-3.5 ${flagged ? 'text-rose-600' : 'text-[#94A3B8]'}`} />
            Flag for revision
          </span>
        </label>
        {flagged && (
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={flagNoteBlur}
            placeholder="QA note (visible to satisFINDER on revision)"
            rows={4}
            className="resize-y"
            data-testid={`qa-flag-note-${qid}`}
          />
        )}
      </div>
    </div>
  );
}

export default function QAReview() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [find, setFind] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState(false);

  const allowed = ['qa', 'platform_admin', 'super_admin'].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/qa/finds/${responseId}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) { toast.error('Access denied'); navigate('/qa-queue'); return; }
        if (res.status === 404) { toast.error('FIND not found'); navigate('/qa-queue'); return; }
        throw new Error(`Failed: ${res.status}`);
      }
      const data = await res.json();
      setFind(data);

      if (data?.qa_status === 'submitted') {
        fetch(`${API}/qa/finds/${responseId}/open`, { method: 'POST', credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d?.qa_status) setFind(prev => ({ ...prev, qa_status: d.qa_status, qa_assigned_to: d.qa_assigned_to })); })
          .catch(() => {});
      }
    } catch {
      toast.error('Could not load FIND');
      navigate('/qa-queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (allowed) load(); /* eslint-disable-next-line */ }, [responseId, allowed]);

  // Hierarchical numbering (Fix 1) — uses shared util.
  const numberMap = useMemo(
    () => computeQuestionNumbers(find?.questions || []),
    [find]
  );

  // Text/comment edit (in-place answers update + qa_edits log)
  const saveTextEdit = async (qid, editedAnswer) => {
    setSavingGlobal(true);
    try {
      const res = await fetch(`${API}/qa/finds/${find.response_id || responseId}/edit-answer`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: qid, edited_answer: editedAnswer }),
      });
      if (!res.ok) { toast.error('Could not save edit'); return false; }
      const data = await res.json();
      // Optimistically update the local copy. The backend has already applied
      // the structure-aware in-place change so we update the same way here.
      setFind(prev => {
        if (!prev) return prev;
        const ans = { ...(prev.answers || {}) };
        const existing = ans[qid];
        if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
          if ('audio_url' in existing || 'transcription' in existing) ans[qid] = { ...existing, transcription: editedAnswer };
          else if ('label' in existing || 'value' in existing || 'comment' in existing || 'score_value' in existing) ans[qid] = { ...existing, comment: editedAnswer };
          else ans[qid] = editedAnswer;
        } else {
          ans[qid] = editedAnswer;
        }
        return { ...prev, qa_edits: data.qa_edits, answers: ans };
      });
      return true;
    } catch {
      toast.error('Could not save edit');
      return false;
    } finally {
      setSavingGlobal(false);
    }
  };

  // Selection edit (Fix 6) — overlay in qa_edits only, raw answers untouched.
  const saveSelectionEdit = async (qid, selection) => {
    if (selection == null) return false;
    setSavingGlobal(true);
    try {
      const res = await fetch(`${API}/qa/finds/${find.response_id || responseId}/edit-answer`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: qid, edited_answer: selection, edit_kind: 'selection' }),
      });
      if (!res.ok) { toast.error('Could not save selection'); return false; }
      const data = await res.json();
      setFind(prev => prev ? ({ ...prev, qa_edits: data.qa_edits }) : prev);
      toast.success('Selection updated');
      return true;
    } catch {
      toast.error('Could not save selection');
      return false;
    } finally {
      setSavingGlobal(false);
    }
  };

  const toggleFlag = async (qid, flagged, qaNote) => {
    try {
      const res = await fetch(`${API}/qa/finds/${find.response_id || responseId}/flag`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: qid, flagged, qa_note: qaNote || '' }),
      });
      if (!res.ok) { toast.error('Could not update flag'); return; }
      const data = await res.json();
      setFind(prev => prev ? ({ ...prev, revision_flags: data.revision_flags }) : prev);
    } catch {
      toast.error('Could not update flag');
    }
  };

  if (!allowed) {
    return (
      <DashboardLayout>
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center" data-testid="qa-review-denied">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
          <p className="font-medium text-[#0F172A]">QA reviewer access required</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading || !find) {
    return (
      <DashboardLayout>
        <div className="text-center py-24 text-[#64748B]" data-testid="qa-review-loading">Loading FIND…</div>
      </DashboardLayout>
    );
  }

  const flagCount = (find.revision_flags || []).filter(f => !f.resolved).length;
  const editCount = (find.qa_edits || []).length;
  const overdue = !!find.tat_overdue;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qa-review-page">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/qa-queue')} data-testid="qa-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Save &amp; Exit
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/qa-queue')} data-testid="qa-return-queue-btn" className="border-[#1A3E6F] text-[#1A3E6F] hover:bg-[#EFF6FF]">
              Return to QA Queue
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#0EA5E9]" />
                <h1 className="font-['Montserrat'] text-xl font-bold text-[#0F172A]">QA Review</h1>
                <span className="font-mono text-xs text-[#64748B] bg-[#F1F5F9] px-2 py-0.5 rounded" data-testid="qa-review-find-id">{find.public_id || find.response_id}</span>
              </div>
              <p className="text-xs text-[#64748B] mt-0.5">
                {find.tenant_name || '—'} · {find.branch_name || '—'} · {find.survey_name || '—'}
              </p>
              {/* Comparison report header — only renders when this FIND has
                  the four comparison answers (survey config OR legacy IDs). */}
              <ComparisonHeader
                answers={find.answers}
                qaEdits={find.qa_edits}
                surveyConfig={find.survey_config}
                variant="inline"
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={find.qa_status} />
            {overdue ? (
              <Badge className="bg-rose-100 text-rose-800 border border-rose-200" data-testid="qa-detail-tat-overdue"><AlertTriangle className="w-3 h-3 mr-1" /> TAT overdue</Badge>
            ) : find.tat_deadline ? (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="qa-detail-tat-ok"><Clock className="w-3 h-3 mr-1" /> TAT {fmtDate(find.tat_deadline)}</Badge>
            ) : null}
            {editCount > 0 && <Badge className="bg-blue-100 text-blue-800 border border-blue-200" data-testid="qa-edit-count">{editCount} edit{editCount === 1 ? '' : 's'}</Badge>}
            {flagCount > 0 && <Badge className="bg-rose-100 text-rose-800 border border-rose-200" data-testid="qa-flag-count"><Flag className="w-3 h-3 mr-1" />{flagCount} flagged</Badge>}
            {savingGlobal && <span className="text-xs text-[#94A3B8] inline-flex items-center gap-1"><Save className="w-3 h-3 animate-pulse" /> Saving…</span>}
          </div>
        </div>

        {/* Meta strip — satisFINDER name section removed until that import flow lands */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Visit</p>
            <p className="text-sm text-[#0F172A]" data-testid="qa-meta-visit">{fmtDate(find.visit_datetime)}</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Submitted</p>
            <p className="text-sm text-[#0F172A]" data-testid="qa-meta-submitted">{fmtDate(find.submission_datetime)}</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Submission window</p>
            <p className="text-sm text-[#0F172A]" data-testid="qa-meta-submission-hours">
              {formatSubmissionWindow(find.satisfinder_submission_hours)}
            </p>
          </div>
        </div>

        {/* Answers */}
        <div className="space-y-4">
          {(find.questions || []).length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center text-[#64748B]" data-testid="qa-no-questions">
              No questions found for this survey configuration.
            </div>
          ) : (
            (find.questions || []).map((q, i) => (
              <AnswerBlock
                key={q.question_id || q.id || i}
                q={q}
                number={numberMap[q.question_id || q.id]}
                answers={find.answers || {}}
                qaEdits={find.qa_edits || []}
                revisionFlags={find.revision_flags || []}
                onSaveTextEdit={saveTextEdit}
                onSaveSelectionEdit={saveSelectionEdit}
                onToggleFlag={toggleFlag}
                busy={savingGlobal}
                find={find}
                responseId={responseId}
                user={user}
                setFind={setFind}
              />
            ))
          )}
        </div>

        {/* DeepQual® Rating intentionally removed from QA Review (Fix 11).
            It will be a separate post-publish step accessible from the
            published FIND record. */}

        {/* satisACTION® */}
        <SatisactionSection find={find} onChange={(patch) => setFind(prev => ({ ...prev, ...patch }))} />

        {/* Action bar (Publish / Reject / Request Revision) */}
        <QAActionsBar find={find} onChange={(patch) => setFind(prev => ({ ...prev, ...patch }))} />

        {/* Footer hint */}
        <div className="text-xs text-[#94A3B8] pt-2">
          Edits auto-save 2 seconds after you stop typing. Selection changes save immediately. Use{' '}
          <span className="font-semibold">Save &amp; Exit</span> to return to the queue — the FIND will remain{' '}
          <span className="font-semibold">In Review</span> until you Publish or Reject.
        </div>
      </div>
    </DashboardLayout>
  );
}
